import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Vendor {
  id: string; code: string;
  name_en: string; name_ar: string | null;
  vendor_type: string;
  city: string | null; phone: string | null;
  cr_number: string | null; vat_number: string | null;
  is_active: boolean;
}

const VENDOR_TYPES = ['in_house', 'workshop', 'parts_supplier', 'tire_shop', 'mobile_mechanic'];

export default function MaintenanceVendorsPage() {
  const [items, setItems] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ nameEn: '', nameAr: '', vendorType: 'workshop', city: '', phone: '', crNumber: '', vatNumber: '' });

  const refresh = useCallback(async () => {
    try {
      const r = await apiClient.get<Vendor[]>('/api/v1/maintenance/vendors');
      setItems(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function submitCreate() {
    if (!form.nameEn) return;
    setSubmitting(true);
    try {
      await apiClient.post('/api/v1/maintenance/vendors', {
        nameEn: form.nameEn,
        nameAr: form.nameAr || undefined,
        vendorType: form.vendorType,
        city: form.city || undefined,
        phone: form.phone || undefined,
        crNumber: form.crNumber || undefined,
        vatNumber: form.vatNumber || undefined,
      });
      setCreating(false);
      setForm({ nameEn: '', nameAr: '', vendorType: 'workshop', city: '', phone: '', crNumber: '', vatNumber: '' });
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setSubmitting(false); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/maintenance"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> Vendors / workshops</div>
          <Button size="sm" onClick={() => setCreating(true)} className="gap-1"><Plus className="h-4 w-4" /> Add vendor</Button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-4 space-y-3">
        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
        : items.length === 0 ? <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No vendors yet.</div>
        : <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">City</th><th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">CR / VAT</th></tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs">{v.code}</td>
                    <td className="px-3 py-2">{v.name_en}{v.name_ar ? ' · ' + v.name_ar : ''}</td>
                    <td className="px-3 py-2 text-xs">{v.vendor_type.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-xs">{v.city ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{v.phone ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{[v.cr_number, v.vat_number].filter(Boolean).join(' · ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </main>

      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Add maintenance vendor</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3">
            <div className="space-y-1"><Label>Name (EN) *</Label><Input value={form.nameEn} onChange={(e) => setForm({...form, nameEn: e.target.value})} /></div>
            <div className="space-y-1"><Label>Name (AR)</Label><Input value={form.nameAr} onChange={(e) => setForm({...form, nameAr: e.target.value})} dir="rtl" /></div>
            <div className="space-y-1">
              <Label>Type *</Label>
              <select value={form.vendorType} onChange={(e) => setForm({...form, vendorType: e.target.value})} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                {VENDOR_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} /></div>
            <div className="space-y-1"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+966…" /></div>
            <div className="space-y-1"><Label>CR number</Label><Input value={form.crNumber} onChange={(e) => setForm({...form, crNumber: e.target.value})} /></div>
            <div className="space-y-1"><Label>VAT number</Label><Input value={form.vatNumber} onChange={(e) => setForm({...form, vatNumber: e.target.value})} /></div>
          </div>
          <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreating(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitCreate} disabled={submitting || !form.nameEn}>{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
